import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../shared/services';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './reset-password.html',
  styleUrls: ['../auth-form.css', './reset-password.css'],
})
export class ResetPassword implements OnDestroy {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private subscription: Subscription;

  resetForm: FormGroup;
  isSubmitting = false;
  token: string | null = null;

  constructor() {
    this.resetForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator }
    );

    this.subscription = this.route.queryParamMap.subscribe((params) => {
      this.token = params.get('token');
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get passwordControl(): FormControl {
    return this.resetForm.get('password') as FormControl;
  }

  get confirmPasswordControl(): FormControl {
    return this.resetForm.get('confirmPassword') as FormControl;
  }

  get isTokenMissing(): boolean {
    return !this.token;
  }

  private passwordMatchValidator(group: FormGroup) {
    const password = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return password && confirm && password === confirm
      ? null
      : { passwordMismatch: true };
  }

  onSubmit(): void {
    if (this.isTokenMissing) {
      this.toastService.show(
        'El enlace de restablecimiento no es válido. Solicita uno nuevo.',
        'warning'
      );
      return;
    }

    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      this.toastService.show(
        'Revisa los campos marcados en rojo antes de continuar.',
        'warning'
      );
      return;
    }

    this.isSubmitting = true;
    const { password } = this.resetForm.value;

    this.authService.resetPassword(this.token!, password).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.toastService.show(
          response?.message ||
            'Tu contraseña se actualizó correctamente. Ya puedes iniciar sesión.',
          'success'
        );
        this.router.navigate(['/auth/signin']);
      },
      error: (err) => {
        this.isSubmitting = false;
        const apiErrors = err?.error?.errors;
        const message =
          (Array.isArray(apiErrors) && apiErrors.length
            ? apiErrors.join('<br>')
            : err?.error?.message) ||
          'No pudimos actualizar tu contraseña. Intenta nuevamente.';
        this.toastService.show(message, 'warning', 6000);
      },
    });
  }
}
